/** @format */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';
import { ENDPOINTS } from 'src/constants';
import type { NewsArticle, NewsDetail, NewsSearchResponse } from 'src/types';

const initialState = {
  newsLoading: false,
  news: null,
  newsDetailLoading: true,
  newsDetail: null as NewsDetail | null,
  newsArticlesLoading: null as boolean | null,
  newsArticles: null as NewsArticle[] | null,
  categoriesLoading: false,
  categories: null,
  updateNewsLoading: false,
  ratingSuccess: false,
  ratingError: null,
  error: null
};

const slice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    getNews(state) {
      state.newsLoading = true;
    },
    getNewsSuccess(state, action) {
      const data = action.payload;
      state.news = data || [];
      state.newsLoading = false;
    },
    getNewsFailure(state, action) {
      state.error = action.payload;
      state.newsLoading = false;
    },
    getNewsArticles(state) {
      state.newsArticlesLoading = true;
    },
    getNewsArticlesSuccess(state, action: PayloadAction<NewsSearchResponse>) {
      const data = action.payload;
      state.newsArticles = data.articles || [];
      state.newsArticlesLoading = false;
    },
    getNewsArticlesFailure(state, action) {
      state.error = action.payload;
      state.newsArticlesLoading = false;
    },
    getCategories(state) {
      state.categoriesLoading = true;
    },
    getCategoriesSuccess(state, action) {
      const data = action.payload;
      state.categories = data.categories || [];
      state.categoriesLoading = false;
    },
    getCategoriesFailure(state, action) {
      state.error = action.payload;
      state.categoriesLoading = false;
    },
    getNewsDetail(state) {
      state.newsDetailLoading = true;
    },
    getNewsDetailSuccess(state, action: PayloadAction<NewsDetail>) {
      const data = action.payload;
      state.newsDetailLoading = false;
      state.newsDetail = data;
    },
    getNewsDetailFailure(state, action) {
      state.error = action.payload;
      state.newsDetailLoading = false;
    },
    rateArticle(state) {
      state.newsDetailLoading = true;
    },
    rateArticleSuccess(state) {
      state.ratingSuccess = true;
      state.newsDetailLoading = false;
    },
    rateArticleFailure(state, action) {
      state.newsDetailLoading = false;
      state.ratingError = action.payload.error;
    },
    clearNewstError(state) {
      state.error = null;
      state.ratingError = null;
      state.ratingSuccess = false;
    },
    resetNewsError(state, action) {
      state.error = action.payload;
      state.newsLoading = false;
    },
    setNewsTitle(state, action: PayloadAction<string>) {
      if (state.newsDetail) {
        state.newsDetail.title = action.payload;
      }
    }
  }
});

export const reducer = slice.reducer;

export const getNews = id => async dispatch => {
  const { getNews, getNewsSuccess, getNewsFailure } = slice.actions;

  try {
    dispatch(getNews());
    // TODO: The endpoint not ready yet
    // const response = await axios.get(`${ENDPOINTS.GET_NEWS_ENDPOINT}/${id}/`);
    // dispatch(getNewsSuccess(response.data));
    dispatch(getNewsSuccess({ id: 3, title: 'General', body: '', categories: [{ id: 73, name: 'General' }] }));
  } catch (err) {
    dispatch(getNewsFailure(err));
  }
};

export const getNewsArticles = params => async dispatch => {
  const { getNewsArticles, getNewsArticlesSuccess, getNewsArticlesFailure } = slice.actions;

  try {
    dispatch(getNewsArticles());
    const response = await axios.get<NewsSearchResponse>(`${ENDPOINTS.GET_NEWS_ENDPOINT}/search/`, { params });
    dispatch(getNewsArticlesSuccess(response.data));
  } catch (err) {
    dispatch(getNewsArticlesFailure(err));
  }
};

export const getNewsCategories = () => async dispatch => {
  const { getCategories, getCategoriesSuccess, getCategoriesFailure } = slice.actions;

  try {
    dispatch(getCategories());
    const response = await axios.get(`${ENDPOINTS.GET_NEWS_CATEGORIES_ENDPOINT}`);
    dispatch(getCategoriesSuccess(response.data));
  } catch (err) {
    dispatch(getCategoriesFailure(err));
  }
};

export const getNewsDetail = (id, noLoad) => async dispatch => {
  const { getNewsDetail, getNewsDetailSuccess, getNewsDetailFailure } = slice.actions;

  try {
    if (!noLoad) dispatch(getNewsDetail());
    const response = await axios.get<NewsDetail>(`${ENDPOINTS.GET_NEWS_ENDPOINT}/${id}`);
    dispatch(getNewsDetailSuccess(response.data));
  } catch (err) {
    dispatch(getNewsDetailFailure(err));
  }
};

export const rateNewsDetail = (nid, rating) => async dispatch => {
  const { rateArticleSuccess, rateArticleFailure } = slice.actions;

  try {
    const formData = new FormData();
    formData.append('newsArticle', nid);
    formData.append('rating', rating);
    await axios.post(ENDPOINTS.GET_NEWS_VOTE_ENDPOINT, formData);
    dispatch(rateArticleSuccess());
    dispatch(getNewsDetail(nid, true));
  } catch (err) {
    dispatch(rateArticleFailure(err));
  }
};

export const clearNewstError = () => dispatch => {
  const { clearNewstError } = slice.actions;
  dispatch(clearNewstError());
};

export const setNewsDetailLoading = () => dispatch => {
  const { getNewsDetail } = slice.actions;
  dispatch(getNewsDetail());
};

export const resetNewsError = () => async dispatch => {
  const { resetNewsError } = slice.actions;
  dispatch(resetNewsError(null));
};

export const setNewsTitle = title => async dispatch => {
  const { setNewsTitle } = slice.actions;
  dispatch(setNewsTitle(title));
};

export default slice;
